/**
 * Connect-time bandwidth probe (PRODUCT path).
 *
 * The existing connection-quality probe (`connectionQualityProbe.ts`) answers "is this link
 * questionable?" from page-load timings frozen at load - which cannot describe the link the
 * stream is about to use, and on Safari can't even be asked. This probe measures the LIVE
 * connection instead: the realtime WebSocket round trip plus a small download-throughput
 * sample taken while the peer stack is still being assembled, so a low-bandwidth link is
 * seated on the 360p tier before the stream even starts rather than demoted mid-session.
 *
 * Browser-free: pure timing inputs + a verdict function. The app measures; this decides.
 */

/** Measured inputs for the connect-time verdict. Both are cheap, live signals. */
export type ConnectBandwidthSample = {
  /**
   * Realtime WebSocket handshake round-trip time (ms), measured from connect() to the
   * server's first answer. The one signal every client can produce, on every engine.
   */
  wsHandshakeRttMs?: number;
  /**
   * Measured throughput of a small signaling payload (Mbps), when a measurable transfer
   * happened during the handshake window. Omitted when nothing measurable was transferred.
   */
  measuredDownlinkMbps?: number;
};

/**
 * A link that cannot carry 480p60 even at the mobile start bitrate is questionable and
 * should be seated on 360p. The mobile tier needs ~2 Mbps to start and ~8 Mbps at ceiling,
 * so the probe's throughput floor sits well under both.
 */
export const CONNECT_QUESTIONABLE_DOWNLINK_MBPS = 2.5;

/**
 * A realtime handshake that takes longer than this is a weak or congested signaling link -
 * and the stream rides the same path, so the verdict applies to the video too. Deliberately
 * above the WebSocket's own ping/pong timing jitter on a healthy LAN (~50-150ms) and below
 * the several-hundred-ms tail a weak radio produces.
 */
export const CONNECT_QUESTIONABLE_WS_RTT_MS = 400;

export type ConnectBandwidthVerdict = 'low-bandwidth' | 'healthy' | 'unknown';

/**
 * Decide the connect-time bandwidth verdict from live measurements.
 *
 * Low-bandwidth wins over healthy: either measurement below its floor is decisive, because
 * the cost of starting on the low tier on a good link is a blurry first frame, while the
 * cost of starting on the high tier on a bad link is a stalled stream.
 */
export function evaluateConnectBandwidth(
  sample: ConnectBandwidthSample,
): ConnectBandwidthVerdict {
  const wsBad =
    sample.wsHandshakeRttMs !== undefined && sample.wsHandshakeRttMs > CONNECT_QUESTIONABLE_WS_RTT_MS;
  const downlinkBad =
    sample.measuredDownlinkMbps !== undefined &&
    sample.measuredDownlinkMbps < CONNECT_QUESTIONABLE_DOWNLINK_MBPS;
  if (wsBad || downlinkBad) return 'low-bandwidth';

  const measured =
    sample.wsHandshakeRttMs !== undefined || sample.measuredDownlinkMbps !== undefined;
  return measured ? 'healthy' : 'unknown';
}
